import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useCallback, useContext, useMemo, useRef } from 'react';

import { TextField, TextFieldRef, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useAuth, useSelectedLocation } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import useRedeliveryApi from 'pages/redeliveries/use-redelivery-api';
import { ActiveUnit, Chassis, Container, Customer, Genset, Location, Redelivery, RedeliveryDetail, Role } from 'types';

import { useNewTransaction } from '..';
import { GateRedeliveryContext } from './redelivery-context';

interface RedeliveryUnit extends ActiveUnit {
  customer: Customer;
  location: Location;
  redelivery: Redelivery;
  sizeType: string;
}

interface RedeliveryNumberProps<T extends Chassis | Container | Genset> {
  unit: T;
  setUnit: Dispatch<SetStateAction<T>>;
  ['data-cy']?: string;
}

const RedeliveryNumber = <T extends Chassis | Container | Genset>({
  unit,
  setUnit,
  ...props
}: RedeliveryNumberProps<T>) => {
  const { userProfile } = useAuth();
  const { getEditable, getGateRedelivery, setEditable, clearRedelivery } = useContext(GateRedeliveryContext);
  const { chassis, container, gateRedeliveryRef, genset, stack, transaction } = useNewTransaction();
  const { findRedelivery, findRedeliveryByUnitId } = useRedeliveryApi();
  const { selectedLocation } = useSelectedLocation();
  const { newZonedDayJs } = useTimeZone();

  const textFieldRef = useRef<TextFieldRef>(null);

  const unitRedelivery = useMemo(() => unit.redelivery, [unit.redelivery]);
  const unitRedeliveryNumber = useMemo(() => unitRedelivery?.redeliveryNumber ?? '', [unitRedelivery]);
  const unitReserved = useMemo(() => !!unitRedelivery?.unitIds?.some(id => id === unit.id), [unit.id, unitRedelivery]);

  const editableUnitRedelivery = useMemo(() => {
    return getEditable(unitRedeliveryNumber, unit?.customer ?? undefined, selectedLocation ?? undefined);
  }, [getEditable, unitRedeliveryNumber, unit?.customer, selectedLocation]);

  const setRedelivery = useCallback(
    (redelivery?: Partial<Redelivery>) => setUnit(prevState => ({ ...prevState, redelivery })),
    [setUnit]
  );
  const setRedeliveryNumber = useCallback(
    (redeliveryNumber: string) => {
      if (!redeliveryNumber || redeliveryNumber === '') {
        const toClear = getGateRedelivery(
          unit.redelivery?.redeliveryNumber,
          unit.customer || undefined,
          transaction.location
        );
        if (!!toClear) clearRedelivery(toClear);
        setRedelivery();
      } else {
        setRedelivery({ redeliveryNumber });
      }
    },
    [
      clearRedelivery,
      getGateRedelivery,
      setRedelivery,
      transaction.location,
      unit.customer,
      unit.redelivery?.redeliveryNumber
    ]
  );

  const getRedeliveryGateUnits = useCallback(
    (redelivery: Redelivery, redeliveryDetail: RedeliveryDetail) => {
      return [chassis, container, genset, ...stack]
        .filter((u): u is RedeliveryUnit => !!u?.customer && !!u?.redelivery && !!u?.sizeType)
        .filter(u => u.customer.id === redelivery.customer?.id)
        .filter(u => u.redelivery.redeliveryNumber === redelivery.redeliveryNumber)
        .filter(u => u.sizeType === redeliveryDetail.sizeType)
        .filter(u => u.unitNumber !== unit.unitNumber);
    },
    [chassis, container, genset, stack, unit.unitNumber]
  );

  const validateRedelivery = useCallback(
    async (input: string): Promise<ValidationResult> => {
      let redelivery: Redelivery | null = null;
      if (unit?.id && unit.id > 0) {
        redelivery = await findRedeliveryByUnitId(unit.id);
      }
      if (!redelivery && !input) {
        return { status: ValidationStatus.DEFAULT };
      } else if (!unit.customer?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Account required for validation' };
      } else if (!selectedLocation?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Location required for validation' };
      }
      if (!redelivery) {
        redelivery = getGateRedelivery(input, unit.customer, selectedLocation);
      }
      if (!redelivery) {
        redelivery = await findRedelivery(input, unit.customer.id, selectedLocation.id);
      }
      if (!redelivery) {
        setEditable({
          redeliveryNumber: input,
          customer: unit.customer,
          location: selectedLocation,
          redeliveryDetails: [{ count: 1, sizeType: unit.sizeType ?? '' } as any]
        });
        return {
          status: ValidationStatus.WARNING,
          error: `Redelivery not found for customer ${unit.customer.name} and location ${selectedLocation.name}`
        };
      }
      const expiration: dayjs.Dayjs = newZonedDayJs(redelivery.expirationDate).endOf('day');
      if (expiration.isBefore(newZonedDayJs(transaction.completedDate))) {
        return { status: ValidationStatus.WARNING, error: `Redelivery expired (${expiration.format('lll')})` };
      }
      const startDate: dayjs.Dayjs = newZonedDayJs(redelivery.startDate).startOf('day');
      if (startDate.isAfter(newZonedDayJs())) {
        return { status: ValidationStatus.WARNING, error: `Redelivery inactive until ${startDate.format('lll')}` };
      }
      if (unit.sizeType) {
        const redeliveryDetail = redelivery.redeliveryDetails?.find(({ sizeType }) => sizeType === unit.sizeType);
        if (!redeliveryDetail) {
          return { status: ValidationStatus.WARNING, error: `Redelivery does not include ${unit.sizeType}` };
        }
        const redeliveryGateUnits = getRedeliveryGateUnits(redelivery, redeliveryDetail);
        if (redeliveryGateUnits.length + (redeliveryDetail.received ?? 0) >= redeliveryDetail.count) {
          setEditable(redelivery);
          return {
            status: ValidationStatus.WARNING,
            error: `Redelivery quantity met (${redeliveryDetail.count ?? 0} x ${redeliveryDetail.sizeType})`
          };
        }
      }
      setRedelivery(redelivery);
      return { status: ValidationStatus.SUCCESS };
    },
    [
      findRedelivery,
      findRedeliveryByUnitId,
      getGateRedelivery,
      getRedeliveryGateUnits,
      newZonedDayJs,
      selectedLocation,
      setEditable,
      setRedelivery,
      transaction?.completedDate,
      unit?.customer,
      unit?.id,
      unit?.sizeType
    ]
  );

  return (
    <div className="d-flex g-2">
      <TextField
        data-cy={props['data-cy']}
        disabled={unitReserved}
        label="REDELIVERY"
        onChange={setRedeliveryNumber}
        ref={textFieldRef}
        validate={validateRedelivery}
        value={unitRedeliveryNumber}
      />
      {!!editableUnitRedelivery &&
        (userProfile.role === Role.ADMIN ||
          userProfile.role === Role.BILLING_ADMIN ||
          userProfile.role === Role.BOOKING ||
          userProfile.role === Role.GATE_BOOKING) && (
          <button
            className="btn-back text-primary"
            data-cy={!props['data-cy'] ? undefined : `${props['data-cy']}-edit-button`}
            type="button"
            onClick={e => {
              e.stopPropagation();
              gateRedeliveryRef.current?.openDialog(editableUnitRedelivery, textFieldRef.current?.revalidate);
            }}
          >
            <span className="material-icons icon-large">{editableUnitRedelivery.id ? 'edit' : 'add_circle'}</span>
          </button>
        )}
    </div>
  );
};

export default RedeliveryNumber;
